import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAdminLoginData } from "../../Utility";
import { getAdminAuth, getAdminAccessModules, setAccessToken, setAdminAuth, setAdminProfile } from "../../utils/auth";
import { onAdminLogin, onAdminLoginReset } from "../../Store/Slices/adminLoginSlice";
import "./LoginPage.css";

function validateAdminLoginForm({ email, password }, setErrors) {
  const newErrors = {};
  if (!email.trim()) newErrors.email = "Email is required";
  else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Email is invalid";
  if (!password) newErrors.password = "Password is required";
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
}

function AdminLoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const adminLoginData = useAdminLoginData();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const isLoading = adminLoginData?.isLoading ?? false;
  const statusCode = adminLoginData?.status_code;
  const isSuccess = statusCode === 200 || statusCode === "200";
  const isError = statusCode != null && !isSuccess;

  useEffect(() => {
    if (getAdminAuth()) {
      const modules = getAdminAccessModules();
      const path = modules?.length ? modules[0] : "/admin/dashboard";
      navigate(path, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (isSuccess) {
      const data = adminLoginData?.data;
      const token =
        data?.token ??
        data?.accessToken ??
        data?.data?.token ??
        data?.data?.accessToken;
      if (token) setAccessToken(token);

      const user = data?.user ?? data;
      const role = (user?.role ?? user?.user_type ?? data?.role ?? data?.user_type ?? "")
        .toString()
        .toLowerCase()
        .trim();
      if (role === "user" || role === "business") {
        setErrors({
          password: "Access denied. This account cannot access the admin panel.",
        });
        dispatch(onAdminLoginReset());
        return;
      }

      const toArray = (v) => {
        if (Array.isArray(v)) return v.map(String).filter(Boolean);
        if (v == null) return [];
        const s = String(v).trim();
        if (!s) return [];
        return s.split(",").map((x) => x.trim()).filter(Boolean);
      };
      const rawModules = user?.access_modules ?? data?.access_modules;
      const rawCities = user?.cities ?? user?.city ?? data?.cities ?? data?.city;
      const accessModules = toArray(rawModules).map((p) => (p.startsWith("/") ? p : `/${p}`));
      const citiesArr = toArray(rawCities);

      if (accessModules.length > 0) {
        setAdminAuth({ access_modules: accessModules, cities: citiesArr });
        setAdminProfile({ email: user?.email, name: user?.name, role, id: user?.id });
        const firstPath = accessModules[0];
        dispatch(onAdminLoginReset());
        navigate(firstPath, { replace: true });
        return;
      }
      setAdminAuth(true);
      setAdminProfile({ email: user?.email, name: user?.name, role, id: user?.id });
      dispatch(onAdminLoginReset());
      navigate("/admin/dashboard", { replace: true });
    }
  }, [isSuccess, adminLoginData?.data, dispatch, navigate]);

  useEffect(() => {
    if (isError) {
      setErrors({ password: adminLoginData?.message ?? "Login failed" });
      dispatch(onAdminLoginReset());
    }
  }, [isError, adminLoginData?.message, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateAdminLoginForm(formData, setErrors)) return;
    dispatch(onAdminLogin({ email: formData.email.trim(), password: formData.password }));
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <div className="admin-login-card">
          <button
            type="button"
            className="admin-login-back"
            onClick={() => navigate("/")}
            aria-label="Back to site"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to site
          </button>
          <div className="admin-login-header">
            <div className="admin-login-icon">🔐</div>
            <h1 className="admin-login-title">Admin Login</h1>
            <p className="admin-login-subtitle">
              Enter your credentials to access the admin panel
            </p>
          </div>
          <form className="admin-login-form" onSubmit={handleSubmit}>
            <div className="admin-form-group">
              <label htmlFor="email" className="admin-form-label">
                Email Address
              </label>
              <div className="admin-input-wrapper">
                <svg
                  className="admin-input-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={`admin-form-input ${errors.email ? "input-error" : ""}`}
                  placeholder="admin@weddingzvenue.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoFocus
                />
              </div>
              {errors.email && (
                <span className="admin-error-message">{errors.email}</span>
              )}
            </div>

            <div className="admin-form-group">
              <label htmlFor="password" className="admin-form-label">
                Password
              </label>
              <div className="admin-input-wrapper admin-input-wrapper-password">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  className={`admin-form-input ${errors.password ? "input-error" : ""}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((p) => !p)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="admin-error-message">{errors.password}</span>
              )}
            </div>

            <div className="admin-forgot-wrap">
              <Link to="/admin/forgot-password" className="admin-forgot-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="admin-login-btn"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminLoginPage;
